import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
// @mui
import { useTheme } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
// theme
import { bgBlur } from "src/theme/css";
// hooks
import { useOffSetTop } from "src/hooks/use-off-set-top";
import { useResponsive } from "src/hooks/use-responsive";
import { useRouter } from "src/routes/hook";
// components
import Logo from "src/components/logo";
import SvgColor from "src/components/svg-color";
import { useSettingsContext } from "src/components/settings";
import Button from "src/consuman-components/generic/button";
import Chronometer from "src/consuman-components/generic/chronometer";
import WorkOrderUserDrawer from "src/layouts/_common/assigned-task-drawer";
import { useSnackbar } from "src/components/snackbar";
import { useTranslation } from "react-i18next";
import { getUserAssigned, update } from "src/api/work-order-task-user";
import { AI_SOURCE_AUDIO, AI_ACTION } from "src/core/lib/constants";
import { useRefs } from "src/consuman-components/generic/context/refsContext";
import SpeechToTextButton from "src/consuman-components/generic/speechToTextButton";
import CategoryTreeDrawer from "src/consuman-components/generic/categories/categories-tree-drawer";
import { getDefaultCategory } from "src/api/category";

// auth
import { useAuthContext } from "src/auth/hooks";

//
import { paths } from "src/routes/paths";
import { HEADER, NAV } from "../config-layout";
import {
  Searchbar,
  AccountPopover,
  SettingsButton,
  NotificationsPopover,
  ChatGPTPopover,
} from "../_common";

// ----------------------------------------------------------------------

export default function Header({ onOpenNav }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { user, hasPermission } = useAuthContext();

  const refs = useRefs();
  const workOrderUserAssignedRef = refs?.workOrderUserAssignedRef;
  const workOrderUserDrawerRef = useRef(null);
  const [assigned, setAssigned] = useState(false);
  const [reload, setReload] = useState(false);

  const categoryDrawer = useRef(null);

  const settings = useSettingsContext();
  const router = useRouter();

  const isNavHorizontal = settings.themeLayout === "horizontal";

  const isNavMini = settings.themeLayout === "mini";

  const lgUp = useResponsive("up", "lg");

  const offset = useOffSetTop(HEADER.H_DESKTOP);

  const offsetTop = offset && !isNavHorizontal;

  const refresh = useCallback(() => {
    setReload(prev => !prev);
  }, []);

  const showPlay = useCallback(() => {}, []);

  const hidePlay = useCallback(() => {}, []);

  useEffect(() => {
    if (!hasPermission("readWorkOrder")) {
      setAssigned(false);
      return;
    }

    getUserAssigned()
      .then(res => {
        setAssigned(res);
      })
      .catch(() => {
        setAssigned(false);
      });
  }, [reload, hasPermission]);

  useEffect(() => {
    if (!workOrderUserAssignedRef) return undefined;

    workOrderUserAssignedRef.current = {
      refresh,
      showPlay,
      hidePlay,
    };

    return () => {
      workOrderUserAssignedRef.current = null;
    };
  }, [workOrderUserAssignedRef, refresh, showPlay, hidePlay]);

  const onStopAssignedWorkOrder = () => {
    update(assigned.id)
      .then(() => {
        refresh();
        enqueueSnackbar(t("saved"), { variant: "success" });
      })
      .catch(error => {
        refresh();
        enqueueSnackbar(t("error"), { variant: "error" });
      });
  };

  const handleTranscription = useCallback(
    async transcription => {
      if (transcription.action) {
        switch (transcription.action.action) {
          case AI_ACTION.workRequest: {
            try {
              const categoryDefault = await getDefaultCategory();

              if (!categoryDefault || categoryDefault.count === 0) {
                enqueueSnackbar(t("noDefaultCategory"), { variant: "warning" });
              } else if (categoryDefault.count === 1) {
                router.replace(paths.dashboard.workRequest.category(categoryDefault.categoryId));
              } else {
                categoryDrawer.current.showDrawer(true);
              }
            } catch {
              // errors are already surfaced by the axios interceptor
            }
            break;
          }
          default:
            enqueueSnackbar(t("error:generic.invalidOperation"), { variant: "info" });
        }
      }
    },
    [t, enqueueSnackbar, router]
  );

  const newWorkRequestRedirect = useCallback(
    category => {
      router.replace(paths.dashboard.workRequest.category(category.id));
    },
    [router]
  );

  const renderContent = (
    <>
      {lgUp && isNavHorizontal && (
        <Logo
          href={paths.dashboard.root}
          sx={{ mr: 2.5 }}
        />
      )}

      {!lgUp && (
        <IconButton onClick={onOpenNav}>
          <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
        </IconButton>
      )}

      <Searchbar />

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1 }}
      >
        {assigned?.id ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              ml: 2,
              mr: 1,
            }}
          >
            <Button
              size="medium"
              color="error"
              tooltip={t("stop")}
              onClick={onStopAssignedWorkOrder}
              icon="material-symbols:stop-circle-outline"
            />
            <Button
              size="medium"
              color="info"
              tooltip={t("workOrderTask")}
              onClick={() => {
                workOrderUserDrawerRef.current?.open();
              }}
              icon="material-symbols:help-outline"
            />
            <Box>
              <Chronometer startDate={new Date(assigned.startDate)} />
            </Box>
          </Stack>
        ) : null}

        {user?.multiEnterprise ? (
          <Chip
            key={user.enterpriseName}
            color="primary"
            variant="soft"
            label={user.enterpriseName}
          />
        ) : null}

        {/* <SpeechToTextButton
          source={AI_SOURCE_AUDIO.action}
          onTranscription={handleTranscription}
        /> */}

        <ChatGPTPopover />

        <NotificationsPopover />

        <SettingsButton />

        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <>
      {assigned?.id ? <WorkOrderUserDrawer ref={workOrderUserDrawerRef} /> : null}

      <CategoryTreeDrawer
        key="category"
        name="category"
        onChange={newWorkRequestRedirect}
        onlyDrawer
        ref={categoryDrawer}
        disableContainer
        disableClearable
        label={t("category")}
      />

      <AppBar
        sx={{
          height: HEADER.H_MOBILE,
          zIndex: theme.zIndex.appBar + 1,
          ...bgBlur({
            color: theme.palette.background.default,
          }),
          transition: theme.transitions.create(["height"], {
            duration: theme.transitions.duration.shorter,
          }),
          ...(lgUp && {
            width: `calc(100% - ${NAV.W_VERTICAL + 1}px)`,
            height: HEADER.H_DESKTOP,
            ...(offsetTop && {
              height: HEADER.H_DESKTOP_OFFSET,
            }),
            ...(isNavHorizontal && {
              width: 1,
              bgcolor: "background.default",
              height: HEADER.H_DESKTOP_OFFSET,
              borderBottom: `dashed 1px ${theme.palette.divider}`,
            }),
            ...(isNavMini && {
              width: `calc(100% - ${NAV.W_MINI + 1}px)`,
            }),
          }),
        }}
      >
        <Toolbar
          sx={{
            height: 1,
            px: { lg: 5 },
          }}
        >
          {renderContent}
        </Toolbar>
      </AppBar>
    </>
  );
}

Header.propTypes = {
  onOpenNav: PropTypes.func,
};
